import React from "react";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ErrorBar,
  Cell,
  Text,
} from "recharts";
import { getColor } from "../tools";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";

function CustomTooltip({ active, payload, labelIds, isAbsolute }) {
  const { t } = useTranslation();
  const payloadArr = payload?.[0]?.payload;
  const name = payloadArr?.["name"];

  if (active) {
    return (
      <Paper style={{ padding: "16px", zIndex: 3 }} elevation={15}>
        <Typography variant="subtitle2">{name}</Typography>
        {labelIds.map(({ id, label }) => {
          const labelValue = payloadArr?.[id];
          const measurements = payloadArr?.[`${id}$numberOfMeasurements`];
          const color = payloadArr?.[`${id}$color`] ?? getColor(id);

          const getNormalText = () => {
            if (isAbsolute) {
              return `${labelValue}`;
            }
            return `${measurements}x ${label}: ${labelValue}%`;
          };

          const getItalicText = () => {
            if (isAbsolute) {
              return "";
            }
            if (measurements <= 5) {
              return `(${t("moodboards.tooFewMeasurements")})`;
            }
            return "";
          };

          if (labelValue !== undefined && measurements !== undefined)
            return (
              <div key={`${id}Tooltip`} style={{ display: "flex" }}>
                <div
                  style={{
                    height: "12px",
                    width: "12px",
                    backgroundColor: color,
                    marginRight: "8px",
                    marginTop: "3px",
                    borderRadius: "50%",
                    display: "flex",
                  }}
                ></div>
                <Typography variant="body2" style={{ marginRight: 3 }}>
                  {getNormalText()}
                </Typography>
                <Typography variant="body2" style={{ fontStyle: "italic" }}>
                  {getItalicText()}
                </Typography>
              </div>
            );
          return <div key={`${id}Tooltip`}></div>;
        })}
      </Paper>
    );
  }
  return null;
}

const CustomizedCursor = (props) => {
  const { x: oX, y: oY, width: oWidth, height: oHeight } = props;
  const fill = "rgba(202, 203, 200, 0.4)";
  return (
    <rect
      fill={fill}
      //fillOpacity={fillOpacity}
      x={oX}
      y={oY}
      width={oWidth}
      height={oHeight}
      rx={10}
      ry={10}
    />
  );
};

// Hatch barchart, solution from: https://alex-taran.blogspot.com/2017/11/recharts-bar-chart-with-multiple-colors.html?m=1
const CandyBar = (props) => {
  const {
    x: oX,
    y: oY,
    width: oWidth,
    height: oHeight,
    fill,
    fillOpacity,
    payload,
    labelId,
    isAbsolute,
  } = props;

  let x = oX;
  let y = oHeight < 0 ? oY + oHeight : oY;
  let width = isNaN(oWidth) ? 0 : oWidth;
  let height = Math.abs(oHeight);
  if (isNaN(y)) y = 0;
  if (isNaN(height)) height = 0;
  const measurement = payload?.[`${labelId}$numberOfMeasurements`];
  // apply hatch to bars if the amount of measurement is less or equal to 5
  if (measurement <= 5 && !isAbsolute)
    return (
      <rect
        fill={fill}
        fillOpacity={fillOpacity}
        mask={"url(#mask-stripe)"}
        x={x}
        y={y}
        width={width}
        height={height}
      />
    );
  // apply no hatch to bars if the amount of measurements is greater than 5
  return (
    <rect
      fill={fill}
      fillOpacity={fillOpacity}
      x={x}
      y={y}
      width={width}
      height={height}
    />
  );
};

function CustomizedLabel(props) {
  const {
    x,
    y,
    width,
    height,
    fillOpacity,
    value,
    vertical,
    showValues,
    thumbnail,
    isAbsolute,
  } = props;
  const oWidth = isNaN(value) ? 0 : width;
  const oHeight = isNaN(value) ? 0 : height;
  const text = isNaN(value)
    ? ""
    : isAbsolute
    ? `${Math.round(value)}`
    : `${Math.round(value)}%`;

  if (vertical && showValues && !thumbnail)
    return (
      <text
        x={x}
        y={y}
        width={oWidth}
        height={oHeight}
        dx={oWidth + 30}
        dy={oHeight / 2 + 3}
        fontSize="12"
        fontFamily="sans-serif"
        fill="#000"
        fillOpacity={fillOpacity}
        textAnchor="middle"
      >
        {text}
      </text>
    );
  if (!vertical && showValues && !thumbnail)
    return (
      <text
        x={x + oWidth / 2}
        y={y}
        width={oWidth}
        height={oHeight}
        dy={-10}
        fontSize="12"
        fontFamily="sans-serif"
        fill="#000"
        fillOpacity={fillOpacity}
        textAnchor="middle"
      >
        {text}
      </text>
    );
  return null;
}

function CustomizedTick(props) {
  const { x, y, angle, payload, lineBreak } = props;
  return (
    <text y={y} x={x} textAnchor="end" fontSize="12">
      {payload.value.length > 11
        ? `${payload.value.substring(0, 11)}...`
        : payload.value}
    </text>
  );

  /*   tickFormatter={(label) => {
    if (label.length > 15) {
      return `${label.substring(0, 50)}...`;
    }
    return `${label}`;
  }}
  width={100} */
}

function CustomizedXAxisTick(props) {
  const { x, y, payload, lineBreak } = props;
  const getValue = () => {
    const label = payload.value;
    if (!lineBreak && label.length > 11) {
      return `${label.substring(0, 11)}...`;
    }
    return `${label}`;
  };
  return (
    <Text
      x={x}
      y={y}
      angle={-35}
      //style={{ fontSize: "16px" }}
      fill="#666"
      textAnchor="end"
      width={lineBreak ? "90" : "150"}
      verticalAnchor="start"
    >
      {getValue()}
    </Text>
  );
}

export default function CustomBarChart(props) {
  const {
    data: d,
    quantitativeLabelsChecked,
    opacity,
    height,
    thumbnail = false,
    error,
    verticalBars = false,
    showValues,
    sortDec,
    lineBreak = false,
    isAbsolute = false,
  } = props;
  const [data, setData] = React.useState(d);
  useEffect(() => {
    if (sortDec) {
      const id = quantitativeLabelsChecked?.[0]?.id;
      const sortedData = d.sort((a, b) => b[id] - a[id]);
      setData(sortedData);
    } else {
      setData(d);
    }
  }, [d, sortDec, quantitativeLabelsChecked]);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={data}
        layout={verticalBars ? "vertical" : "horizontal"}
        margin={{
          top: 10,
          right: 25,
          left: thumbnail ? 0 : 0,
          bottom: 15,
        }}
        cursor={thumbnail ? "pointer" : "default"}
        maxBarSize={50}
      >
        <pattern
          id="pattern-stripe"
          width="8"
          height="8"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <rect
            width="6"
            height="8"
            transform="translate(0,0)"
            fill="white"
          ></rect>
        </pattern>
        <mask id="mask-stripe">
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill={"url(#pattern-stripe)"}
          />
        </mask>
        <CartesianGrid vertical={verticalBars} horizontal={!verticalBars} />

        {!thumbnail ? (
          <Tooltip
            wrapperStyle={{ top: -100 }}
            cursor={<CustomizedCursor />}
            //corusor={{ fill: "rgba(202, 203, 200, 0.4)", borderRadius: "50%" }}
            content={
              <CustomTooltip
                labelIds={quantitativeLabelsChecked}
                isAbsolute={isAbsolute}
              />
            }
          />
        ) : null}
        {quantitativeLabelsChecked.map(({ id, label }) => (
          <Bar
            key={id}
            isAnimationActive={false}
            dataKey={id}
            fillOpacity={`${opacity ? opacity[id] : 1}`}
            shape={<CandyBar labelId={id} isAbsolute={isAbsolute} />}
            label={
              <CustomizedLabel
                showValues={showValues}
                fillOpacity={`${opacity ? opacity[id] : 1}`}
                vertical={verticalBars}
                labelsLength={quantitativeLabelsChecked.length}
                thumbnail={thumbnail}
                isAbsolute={isAbsolute}
              />
            }
          >
            {error ? (
              <ErrorBar
                dataKey={`${id}$error`}
                width={0}
                //strokeWidth={error && opacity[label] !== 0.15 ? 1 : 0}
                strokeWidth={1}
                //opacity={`${opacity && !thumbnail ? opacity[label] : 1}`}
                stroke="#4D4B4B"
              />
            ) : null}
            {data.map((entry, index) => (
              <Cell key={index} fill={entry?.[`${id}$color`] ?? getColor(id)} />
            ))}
          </Bar>
        ))}
        {!thumbnail && verticalBars ? (
          <YAxis
            dataKey="name"
            type="category"
            axisLine={true}
            tickLine={false}
            width={90}
            tick={<CustomizedTick lineBreak={lineBreak} />}
            interval={0}
          />
        ) : null}

        {!thumbnail && !verticalBars ? (
          <YAxis
            domain={
              isAbsolute
                ? [(dataMin) => 0, (dataMax) => Math.round(dataMax / 5) * 5 + 5]
                : [(dataMin) => 0, (dataMax) => 100]
            }
            scale="linear"
            ticks={isAbsolute ? [] : [0, 25, 50, 75, 100]}
            unit={isAbsolute ? "" : "%"}
            axisLine={true}
          />
        ) : null}

        {!thumbnail && verticalBars ? (
          <XAxis
            axisLine={true}
            type="number"
            domain={
              isAbsolute
                ? [(dataMin) => 0, (dataMax) => Math.round(dataMax / 5) * 5 + 5]
                : [(dataMin) => 0, (dataMax) => 100]
            }
            unit={isAbsolute ? "" : "%"}
          />
        ) : null}
        {!thumbnail && !verticalBars ? (
          <XAxis
            tick={<CustomizedXAxisTick lineBreak={lineBreak} />}
            dataKey="name"
            textAnchor="end"
            interval={0}
            angle={-35}
            height={90}
            axisLine={true}
          />
        ) : null}
      </BarChart>
    </ResponsiveContainer>
  );
}
